import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { EventEmitter, Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { NmsWizardStepValidationResponse } from "@nms-angular-toolkit/nms-wizard";
import {
    ANGULARJS_ROOTSCOPE,
    ANGULARJS_TRANSLATE,
    NMS_STATES,
    STATE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { NmsToasterLink } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr-model";
import { NmsToastrLinkService } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr-link.service";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import {
    ErrorResponseHandlerService
 } from "@nms-ng2/app/core/services/error-response-handler/error-response-handler.service";
import { EndLink, Link, LinkInfo, LinkType, MuxDemux, Slot } from "../links.model";
import { DeviceLinkModel, LinkEnds } from "../modal-wizard/steps/1-devices/link-device-model";

/**
 * Serviço responsável por realizar as requisições referentes aos Links.
 */
@Injectable({
    providedIn: "root"
})
export class LinkService {

    private baseUrl: string;
    private readonly CLOSE: boolean = true;
    private readonly RELOAD: boolean = true;
    private readonly CONTENT_TYPE_APPLICATION_JSON = "application/json";
    closeModalEmmiter = new EventEmitter<boolean>();
    reloadListEmmiter = new EventEmitter<boolean>();

    constructor(private http: HttpClient,
                private nmsToastrLinkService: NmsToastrLinkService,
                private errorResponseHandler: ErrorResponseHandlerService,
                private nmsDialogService: NmsDialogService,
                @Inject(ANGULARJS_TRANSLATE) private translate,
                @Inject(ANGULARJS_ROOTSCOPE) private $rootScope: any,
                @Inject(STATE) private $state: any,
                @Inject(NMS_STATES) private nmsStates: any) {
        this.baseUrl = "/topology/link";
    }

    getSlotPorts(resourceId): Observable<Array<Slot>> {
        const slots = this.http.get<Array<Slot>>(`${this.baseUrl}/slots/` + resourceId);
        slots.subscribe({
            error: (error: HttpErrorResponse) => this.showErrorLinkMessage(error)
        });

        return slots;
    }

    getMuxes(resourceId): Observable<Array<MuxDemux>> {
        const muxes = this.http.get<Array<MuxDemux>>(`${this.baseUrl}/muxes/` + resourceId);
        muxes.subscribe({
            error: (error: HttpErrorResponse) => this.showErrorLinkMessage(error)
        });

        return muxes;
    }

    getLinkTypes(linksEnds: LinkEnds): Observable<Array<LinkType>> {
        const url = `${this.baseUrl}/type`;
        const linkTypes = this.http.post<Array<LinkType>>(url, linksEnds);
        linkTypes.subscribe({
            error: (error: HttpErrorResponse) => this.showErrorLinkMessage(error)
        });

        return linkTypes;
    }

    async saveLink(link: Link): Promise<void> {
        try {
            const httpOptions = {
                headers: new HttpHeaders({ "Content-Type": this.CONTENT_TYPE_APPLICATION_JSON })
            };
            await this.http.post<Link>(`${this.baseUrl}`, link, httpOptions).toPromise();

            this.showSuccessLinkMessage("links.modal.save.successfully", this.createSuccessDetailsLink());

            return Promise.resolve();
        } catch(error) {
            let message: string = this.translate.instant("links.modal.add.error") + "<br>";
            this.showErrorLinkMessage(error, message);

            return Promise.reject();
        }
    }

    async updateLink(link: Link): Promise<void> {
        try {
            const httpOptions = { headers: new HttpHeaders({ "Content-Type": this.CONTENT_TYPE_APPLICATION_JSON }) };
            await this.http.put<Link>(`${this.baseUrl}`, link, httpOptions).toPromise();

            this.showSuccessLinkMessage("links.modal.update.successfully");

            Promise.resolve();
        } catch(error) {
            let message: string = this.translate.instant("links.modal.update.error") + "<br>";
            this.showErrorLinkMessage(error, message)

            return Promise.reject();
        }
    }

    removeLinks(linksInfo: LinkInfo[]) {
        const linkIds: number[] = linksInfo.map((item) => item.id);
        const httpOptions = { headers: new HttpHeaders({ "Content-Type": this.CONTENT_TYPE_APPLICATION_JSON }), body: linkIds };
        this.http.delete(`${this.baseUrl}`, httpOptions).subscribe(
            () => this.showSuccessRemoveLinkMessage(linksInfo.length),
            (error: HttpErrorResponse) => {
                const isMultiple = linksInfo.length > 1;
                const prefix = isMultiple ? "links.modal.remove.multiple.error" : "links.modal.remove.single.error";
                let message: string = this.translate.instant(prefix) + "<br>";

                if (isMultiple) {
                    message += linksInfo.map(link => link.name).join("<br>");
                    this.showMessage(message);
                } else {
                    this.showErrorLinkMessage(error, message);
                }

            }
        );
    }

    getLinkById(linkId: number): Observable<Link> {
        return this.http.get<Link>(`${this.baseUrl}/${linkId}`);
    }

    isDwdmSupported(modelName: string): Observable<boolean> {
        return this.http.get<boolean>(`${this.baseUrl}/is-dwdm-supported`, { params: { modelName } });
    }

    generateName(deviceLinkModel: DeviceLinkModel): Observable<string> {
        const linkEnds = this.buildDeviceLinkToGenerateName(deviceLinkModel);

        return this.http.post<string>(`${this.baseUrl}/generate-name`, linkEnds, { responseType: "text" as "json" });
    }

    getPortMembers(resourceId: number, portChannel: number): Observable<Array<string>> {
        return this.http.get<Array<string>>(`${this.baseUrl}/port-members/${resourceId}/${portChannel}`);
    }

    async validateEnds(ends: LinkEnds): Promise<NmsWizardStepValidationResponse> {
        try {
            await this.http.post<void>(`${this.baseUrl}/validate-ends`, ends).toPromise();
            return Promise.resolve(new NmsWizardStepValidationResponse(true));
        } catch(error) {
            return Promise.reject(new NmsWizardStepValidationResponse(false, this.buildErrorMessage(error)));
        }
    }

    private showSuccessLinkMessage(prefix: string, messageLinks?: NmsToasterLink[]) {
        this.reloadListEmmiter.emit(this.RELOAD);
        let message: string = this.translate.instant(prefix);

        this.nmsToastrLinkService.showToastSuccess(message, messageLinks);
        this.closeModalEmmiter.emit(this.CLOSE);
    }

    private showSuccessRemoveLinkMessage(amountLinks: number) {
        this.reloadListEmmiter.emit(this.RELOAD);
        const message = amountLinks == 1
            ? this.translate.instant("table.links.remove.single.successfully")
            : this.translate.instant("table.links.remove.plural.successfully").replace("{0}", amountLinks);
        this.nmsToastrLinkService.showToastSuccess(message, null);
    }

    private createSuccessDetailsLink(): NmsToasterLink[] {
        var link = {
            id: "links-id",
            title: this.translate.instant("links.modal.see.links"),
            action: () => this.$state.go(this.nmsStates.links, {}, {reload:true})
        };

        return [link];
    }

    private buildErrorMessage(error: HttpErrorResponse, message = "") {
        const { description, details } = this.errorResponseHandler.buildErrorDescriptionDetails(error.error);

        return `${message}${description}${details}`;
    }

    private showErrorLinkMessage(error: HttpErrorResponse, message = "") {
        this.showMessage(this.buildErrorMessage(error, message));
    }

    private showMessage(message: string) {
        this.nmsDialogService.openDialog(
            { description: message, acceptLabel: "OK", bindHtml: true },
            { maxWidth: "800px" }
        );
    }

    private buildDeviceLinkToGenerateName(deviceLinkModel: DeviceLinkModel): LinkEnds {
        let aEndLink = new EndLink(deviceLinkModel.aEndLink.endResourceId, deviceLinkModel.aEndLink.endChassisNo,
            deviceLinkModel.aEndLink.endSlotId, Number(deviceLinkModel.aEndLink.endPortNo),
            deviceLinkModel.aEndLink.endPortModelId, deviceLinkModel.aEndLink.endTransmission,
            deviceLinkModel.aEndLink.endMuxResourceId, deviceLinkModel.aEndLink.muxDemux
        );
        let zEndLink = new EndLink(deviceLinkModel.zEndLink.endResourceId, deviceLinkModel.zEndLink.endChassisNo,
            deviceLinkModel.zEndLink.endSlotId, Number(deviceLinkModel.zEndLink.endPortNo),
            deviceLinkModel.zEndLink.endPortModelId, deviceLinkModel.zEndLink.endTransmission,
            deviceLinkModel.zEndLink.endMuxResourceId, deviceLinkModel.zEndLink.muxDemux
        );
        return new LinkEnds(aEndLink, zEndLink);
    }

}
